import { useLayoutEffect, useRef, useState } from "react";
import type { RequestDetail } from "./api";
import { money, tokens } from "./format";
import { billedCost, duration, INPUT_CLASSES, inputTokens, labelledGaps, layout, OTHER_MODEL, roundedTop } from "./timeline";

const H = { input: 150, output: 44, cost: 44 };
const STRIP = 6;
const PANEL_GAP = 22;
const TOP = 16;

/**
 * One session as three small charts sharing one x axis (request order): input tokens by class,
 * output tokens, and cost. Separate panels rather than a second y axis, because the three differ by
 * orders of magnitude. Every bar is a button-sized hit target; the selected one is outlined.
 */
export function SessionTimeline({ rows, selected, onSelect, highlight }: {
  rows: RequestDetail[];
  selected: number;
  onSelect: (i: number) => void;
  highlight: Set<string>;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, Math.floor(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const g = layout(rows, width, H);
  const labelled = labelledGaps(g.gaps, width);
  const yOut = TOP + H.input + STRIP + PANEL_GAP;
  const yCost = yOut + H.output + PANEL_GAP;
  const height = yCost + H.cost + 4;
  const models = [...g.modelColor].filter(([, c]) => c !== OTHER_MODEL);
  const others = [...g.modelColor].some(([, c]) => c === OTHER_MODEL);
  const tip = hover ?? null;
  const tipRow = tip === null ? null : rows[tip]!;
  const tipBar = tip === null ? null : g.bars[tip]!;

  return (
    <div className="timeline" ref={wrap}>
      <div className="timeline__legend" aria-hidden="true">
        {INPUT_CLASSES.map((c) => <span key={c.key}><i style={{ background: c.color }} />{c.label}</span>)}
        <span className="timeline__legend-sep" />
        {models.map(([m, c]) => <span key={m}><i className="timeline__legend-strip" style={{ background: c }} />{m}</span>)}
        {others && <span><i className="timeline__legend-strip" style={{ background: OTHER_MODEL }} />other models</span>}
      </div>
      <svg width={width} height={height} role="img" className="timeline__svg"
        aria-label={`Timeline of ${rows.length} requests. Largest input ${tokens(g.maxInput)} tokens; highest cost ${money(g.maxCost)}. Use the table view for exact values, or J and K to step through requests.`}>
        <line x1={0} x2={width} y1={TOP + H.input} y2={TOP + H.input} className="timeline__baseline" />
        <line x1={0} x2={width} y1={yOut + H.output} y2={yOut + H.output} className="timeline__baseline" />
        <line x1={0} x2={width} y1={yCost + H.cost} y2={yCost + H.cost} className="timeline__baseline" />

        {g.gaps.map((gp) => (
          <g key={gp.index} className="timeline__gap">
            <line x1={gp.x} x2={gp.x} y1={TOP + (labelled.has(gp.index) ? 0 : 14)} y2={yCost + H.cost} />
            {labelled.has(gp.index) && <text x={gp.x + 3} y={TOP + 10}>{duration(gp.ms)} idle</text>}
          </g>
        ))}

        {g.bars.map((b, i) => {
          const r = rows[i]!;
          const dim = highlight.size > 0 && !highlight.has(r.requestId);
          const top = b.segments.length ? b.segments[b.segments.length - 1]! : null;
          return (
            <g key={r.requestId} className={`timeline__bar${i === selected ? " is-selected" : ""}${dim ? " is-dim" : ""}`}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onSelect(i)}>
              <rect x={b.bandX} y={TOP} width={b.bandWidth} height={height - TOP} className="timeline__hit" />
              {b.segments.map((s) => (
                s === top
                  ? <path key={s.cls} d={roundedTop(b.x, TOP + s.y, b.width, s.h)} fill={INPUT_CLASSES.find((c) => c.key === s.cls)!.color} />
                  : <rect key={s.cls} x={b.x} y={TOP + s.y} width={b.width} height={s.h} fill={INPUT_CLASSES.find((c) => c.key === s.cls)!.color} />
              ))}
              <rect x={b.bandX} y={TOP + H.input + 2} width={Math.max(1, b.bandWidth)} height={STRIP - 2} fill={g.modelColor.get(r.model)} />
              {r.status !== "success" && <circle cx={b.x + b.width / 2} cy={TOP + H.input - 4} r={3} className="timeline__fail" />}
              {b.outputH > 0 && <path d={roundedTop(b.x, yOut + H.output - b.outputH, b.width, b.outputH)} className="timeline__ink" />}
              {b.costH > 0 && <path d={roundedTop(b.x, yCost + H.cost - b.costH, b.width, b.costH)} className="timeline__ink" />}
              {i === selected && (() => {
                // Hug the bar (with a little air), never wider than its band.
                const w = Math.min(b.bandWidth - 1, b.width + 8);
                return <rect x={b.x + b.width / 2 - w / 2} y={TOP - 4} width={Math.max(2, w)} height={height - TOP + 2} className="timeline__cursor" />;
              })()}
            </g>
          );
        })}
        {/* Drawn last so bars and the cursor never cover them. */}
        <g className="timeline__labels" aria-hidden="true">
          <text x={0} y={10} className="timeline__panel-label">Input tokens · up to {tokens(g.maxInput)}</text>
          <text x={0} y={yOut - 6} className="timeline__panel-label">Output tokens · up to {tokens(g.maxOutput)}</text>
          <text x={0} y={yCost - 6} className="timeline__panel-label">Cost (est.) · up to {money(g.maxCost)}</text>
        </g>
      </svg>
      {tipRow && tipBar && (
        <div className="timeline__tip" role="status"
          style={{ left: Math.min(Math.max(0, tipBar.x + tipBar.width / 2 - 110), width - 220) }}>
          <b>#{tip! + 1} · {tipRow.model}</b>
          <span>{new Date(tipRow.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })}
            {tipRow.status !== "success" ? " · failed" : tipRow.cacheHit ? " · response cache hit" : ""}</span>
          <span>Input {tokens(inputTokens(tipRow))}: {tokens(tipRow.tokens.cacheRead)} cached, {tokens(tipRow.tokens.cacheWrite)} written, {tokens(tipRow.tokens.text)} new</span>
          <span>Output {tokens(tipRow.tokens.output)} · {billedCost(tipRow) === 0 && tipRow.cacheHit ? "$0" : money(billedCost(tipRow))}</span>
        </div>
      )}
    </div>
  );
}
